import App from 'app/app.js';
import 'app/action/ResetJobAction.js';
import 'app/action/DeserializeAction.js';
import 'app/action/SessionTimeoutAction.js';

// eslint-disable-next-line max-lines-per-function
function StorageService(
  ResetJobAction,
  SessionTimeoutAction,
  DeserializeAction,
  CachedArrayDataFactory,
  loggerService,
  FlightRecorderService,
  $injector,
  $window
) {
  const MILLIS_IN_30_MINUTES = 30 * 60 * 1000;
  // MLC-13290: fallback for window.sessionSyncJobMaxAgeInMs, used when the runtime config row is
  // absent or unparseable.
  const DEFAULT_JOB_MAX_AGE_MILLIS = 8 * 60 * 60 * 1000;
  const KEY_PREFIX = 'mlt.';
  const DATA_PREFIX = 'mlt.data.';
  const VERSION_KEY = 'mlt.version';
  const LAST_ACCESS_KEY = 'mlt.last.access';
  // MLC-13290: the server session instance the stored data was obtained from. Cleared by
  // resetLocal along with the data it describes, and rewritten immediately after — the two must
  // never outlive each other.
  const SESSION_INSTANCE_ID_KEY = 'mlt.session.instanceId';

  /**
   * Keys inside the mlt. namespace that another writer owns. EnhancementStore syncs
   * mlt.data.enhancementStore straight to localStorage via NgRx withStorageSync
   * (store/data/enhancement/enhancement.store.ts:17), so its writes never reach _write. Caching one
   * would go stale the next time that writer updates it, and _read would serve the stale copy
   * forever — so these are always read through to localStorage. They are still enumerated, so
   * resetLocal continues to clear them.
   */
  const EXTERNALLY_MANAGED_KEYS = new Set(['mlt.data.enhancementStore']);

  /**
   * Statsd counter per reset reason — answerable from a dashboard, not just a flight recorder.
   * Codes must exist in StatsCounters.CLIENT_CODES or stats/count 404s.
   *
   * version/idle are omitted: they fire for every user on every deploy or break and would bury
   * the diagnostic reasons below.
   */
  const RESET_COUNTERS = {
    maxAge: 'SESSION_SYNC_RESET_MAX_AGE',
    'outOfSync:session': 'SESSION_SYNC_RESET_SESSION',
    'outOfSync:jobs': 'SESSION_SYNC_RESET_JOBS',
    'outOfSync:imageAuthorization': 'SESSION_SYNC_RESET_IMAGE_AUTH',
    'outOfSync:unknown': 'SESSION_SYNC_RESET_UNKNOWN'
  };

  const _inMemoryStorage = new Map();
  const _reportedStorageLoss = new Set();
  let _persistenceDisabled = false;
  let _logging = false;

  /**
   * A failure raised while logging must not log again. LoggerService used to read the app version
   * back out of this service, which made a corrupt mlt.version recurse until the stack blew; it now
   * reads #app-version directly, so the cycle is closed at the source. This guard stays as the cheap
   * backstop against any future logger that reads storage.
   */
  const logError = (message, extra) => {
    if (_logging) {
      return;
    }

    _logging = true;
    try {
      loggerService.error(message, extra);
    } finally {
      _logging = false;
    }
  };

  /**
   * A key the mirror holds that localStorage no longer does means storage was cleared by something
   * other than this service — Safari ITP deleting website data is the known cause, and it leaves no
   * error or status code behind. This is the one direct signal that it happened. Once per key.
   */
  const reportKeyLost = (key) => {
    if (_persistenceDisabled || _reportedStorageLoss.has(key)) {
      return;
    }

    _reportedStorageLoss.add(key);
    logError(`StorageService: ${key} lost from localStorage`, {
      key,
      flightRecorder: FlightRecorderService.records()
    });
  };

  this.set = (key, value) => {
    this.updateLastAccess();
    if (value !== undefined) {
      this._write(KEY_PREFIX + key, value);
    } else {
      this.remove(key);
    }
  };

  this.get = (key) => {
    this.updateLastAccess();
    return this._read(KEY_PREFIX + key);
  };

  this.remove = (key) => {
    this.updateLastAccess();
    this._delete(KEY_PREFIX + key);
  };

  /**
   * Parses and stores the current jobs in localstorage. Then searches through the parsed array to find the
   * matching job by the jobKey. If it is found, it deletes the orphaned job and sets the local storage to no longer
   * contain that job in mlt.data.jobData.
   * @param jobKey
   */
  this.removeJob = (jobKey) => {
    const jobs = this.getData('jobData');

    if (!jobs || !jobKey) {
      return;
    }

    const jobToDeleteIndex = jobs.findIndex((job) => job?.jobContext?.jobKey === jobKey);

    if (jobToDeleteIndex >= 0) {
      jobs.splice(jobToDeleteIndex, 1);
      this.setData('jobData', jobs);
    }
  };

  this.updateLastAccess = () => {
    this._write(LAST_ACCESS_KEY, new Date().getTime());
  };

  this.getLastAccess = () => this._read(LAST_ACCESS_KEY);

  this.setDictionaryData = (key, itemKey, data) => {
    this.setData(key, CachedArrayDataFactory.setDictionaryData(itemKey, data, this.getData(key)));
  };

  this.getDictionaryData = (key, itemKey) => CachedArrayDataFactory.getDictionaryData(itemKey, this.getData(key));

  this.setData = (key, data, opts, appendableData) => {
    if (key === 'jobData') {
      FlightRecorderService.log(`StorageService.setData:${key}`);
    }
    if (!data) {
      this.remove(`data.${key}`);
      return;
    }

    if (!opts) {
      this.set(`data.${key}`, data);
      return;
    }

    this.setData(key, CachedArrayDataFactory.handleDataArrayToCache(data, opts, this.getData(key), appendableData));
  };

  this.getData = (key, id, opts) => {
    if (!opts) {
      return this.get(`data.${key}`);
    }

    return CachedArrayDataFactory.getArrayObjectById(id, opts, this.getData(key));
  };

  this.reset = () => {
    // clear local storage on frontend
    this.resetLocal();
    // clear local storage on backend
    this.resetServer();
  };

  this.resetServer = () => $injector.get('$http').post('localStorage/clear');

  this.resetLocal = () => {
    FlightRecorderService.log('StorageService.resetLocal');

    this._findKeysStartingWith(KEY_PREFIX).forEach((key) => {
      if (key !== VERSION_KEY) {
        this._delete(key);
      }
    });

    // Space may have been freed, so give persistence another chance. After the enumeration above,
    // otherwise keys we deliberately stopped writing would be reported as storage loss.
    _persistenceDisabled = false;
  };

  this.getVersion = () => this._read(VERSION_KEY);

  this.setVersion = (version) => {
    this.set('version', version);
  };

  this.deserialize = () => {
    const data = Object.fromEntries(
      this._findKeysStartingWith(DATA_PREFIX).map((key) => {
        const dataKey = key.slice(DATA_PREFIX.length);
        return [dataKey, this.getData(dataKey)];
      })
    );

    DeserializeAction.dispatch(data);
  };

  /**
   * @param version                    the deployed app version
   * @param serverSessionState         the live session's identity/contents, from
   *                                   SessionSyncService.fetchServerSessionState. null = could
   *                                   not be determined; undefined = not asked (SESSION_SYNC flag
   *                                   off), which skips the checks below.
   * @param precapturedLastAccessTime  optional. MLC-13360: this function runs after the
   *                                   serverSessionState fetch, by which point other async
   *                                   bootstrap work may have already re-stamped mlt.last.access
   *                                   and defeated the idle check. InitService.init() reads it
   *                                   up front and passes it in here. Omit to fall back to
   *                                   getLastAccess() (unchanged prior behavior).
   *
   * Four independent triggers, none subsuming another:
   *   version   stored data's shape no longer matches the running JS
   *   idle      30 minutes of inactivity
   *   maxAge    a job's total lifetime, however active the user has been
   *   outOfSync the session behind the data is gone, or no longer matches it
   *
   * getLastAccess() must run first: it and the stored-state read below both touch storage, and
   * any get/set stamps the idle clock — reading in the other order would reset it before it's
   * measured, so stale data would never be discarded. (N/A when precapturedLastAccessTime is
   * given — the caller already handled this.)
   */
  this.resetDataIfNecessary = (version, serverSessionState, precapturedLastAccessTime) => {
    const lastAccessTime = precapturedLastAccessTime === undefined ? this.getLastAccess() : precapturedLastAccessTime;
    const storedVersion = this.getVersion();
    // One read feeding both MLC-13290 checks — jobData carries whole product catalogs, so it is
    // not cheap to parse twice. Skipped entirely when the feature is off.
    const stored = serverSessionState === undefined ? null : this.getStoredSessionState();

    const versionChanged = version !== storedVersion;
    // don't load local storage that is more than 30 minutes old
    const idle = new Date().getTime() - lastAccessTime >= MILLIS_IN_30_MINUTES;
    const jobExpired = !!stored && this._hasExpiredJob(stored);
    const syncMismatch = stored ? this._findSyncMismatch(stored, serverSessionState) : null;
    const resetRequired = versionChanged || idle || jobExpired || !!syncMismatch;

    if (resetRequired) {
      // which trigger fired is the question every storage-loss report has to answer
      const reason = versionChanged ? 'version' : idle ? 'idle' : jobExpired ? 'maxAge' : syncMismatch;

      FlightRecorderService.log(`StorageService.resetDataIfNecessary:${reason}`);
      this._countReset(reason);
      this.discardStaleData(`resetDataIfNecessary:${reason}`);
      this.setVersion(version);
      this.retrieveStorage();
    }

    // After the discard, not before: resetLocal clears every mlt. key but the version, so binding
    // storage to this session has to be the last write. Runs on both paths — an unchanged session
    // rewrites the same value, and a first-time visitor is bound here rather than on their next
    // visit.
    this.setSessionInstanceId(serverSessionState?.instanceId);

    return resetRequired;
  };

  /**
   * Reports a reset to statsd; reasons with no RESET_COUNTERS entry (version, idle) are skipped.
   *
   * Resolved via $injector rather than injected, same as $http elsewhere in this file: it avoids
   * a construction-time cycle through any interceptor that reads storage.
   *
   * Never throws — this runs inside InitService.init(), where an uncaught error would leave the
   * splash screen up. The .catch covers the promise, the try/catch covers resolution itself.
   */
  this._countReset = (reason) => {
    const code = RESET_COUNTERS[reason];

    if (!code) {
      return;
    }

    try {
      $injector.get('StatsdService').incrementCounter(code).catch(angular.noop);
    } catch (e) {
      logError(`StorageService: could not count reset ${code}`, e);
    }
  };

  /**
   * Records which server session the stored data belongs to. No-ops without an id (fetch failed,
   * or feature off) rather than storing a null a later comparison would have to special-case.
   */
  this.setSessionInstanceId = (instanceId) => {
    if (instanceId) {
      this._write(SESSION_INSTANCE_ID_KEY, instanceId);
    }
  };

  /**
   * The age at which a stored job is discarded no matter how recently it was used.
   *
   * Read per call, not at construction: the value arrives from MLT_RUNTIME_CONFIG as a String, and
   * a missing or garbled row must fall back rather than silently expire everything.
   */
  this._getJobMaxAgeMillis = () => {
    const configured = Number($window.sessionSyncJobMaxAgeInMs);

    return Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_JOB_MAX_AGE_MILLIS;
  };

  /**
   * Whether any stored job has outlived the max age — total lifetime, unlike the idle check above
   * which only caps inactivity. A job kept "warm" by a day of activity can still be rehydrated
   * against catalog/pricing state that has since moved, mis-pricing the cart it's in.
   *
   * Jobs with no loadedAt stamp (stored before this shipped) are left out here and expire via
   * version/idle instead, so nobody's cart is discarded on deploy day.
   */
  this._hasExpiredJob = (stored) => {
    const now = new Date().getTime();
    const maxAgeMillis = this._getJobMaxAgeMillis();

    return stored.jobLoadTimes.some((loadedAt) => now - loadedAt >= maxAgeMillis);
  };

  /**
   * Why locally persisted server-derived state doesn't match this session, or null if it does.
   *
   * @return null | 'outOfSync:unknown' | 'outOfSync:session' | 'outOfSync:jobs'
   *         | 'outOfSync:imageAuthorization'
   *
   * Nothing stored means nothing to be out of sync with — a first-time visitor is never reset. A
   * failed fetch (serverSessionState null) counts as out of sync: a wrongly discarded cart is
   * recoverable, a wrongly rehydrated one ships a premium background nobody paid for, so this
   * fails closed.
   */
  this._findSyncMismatch = (stored, serverSessionState) => {
    if (!stored.jobNumbers.length && !stored.imageAuthorizationUrls.length) {
      return null;
    }

    if (!serverSessionState) {
      return 'outOfSync:unknown';
    }

    // Primary check: a new session is a new instanceId regardless of contents. Only fires when
    // BOTH sides have one — a missing stored id means storage was never bound to a session (fetch
    // failed, or feature was off at the time), which is not evidence of a mismatch.
    if (stored.instanceId && serverSessionState.instanceId && stored.instanceId !== serverSessionState.instanceId) {
      return 'outOfSync:session';
    }

    // Catches state that can change within one session without a new instanceId: cart/
    // clearSessionData, a premium background flag flipped on after job load, a re-minted IRIS
    // authorization.
    const serverJobNumbers = serverSessionState.jobNumbers || [];

    if (stored.jobNumbers.some((jobNumber) => !serverJobNumbers.includes(jobNumber))) {
      return 'outOfSync:jobs';
    }

    // One-directional: the server holding an authorization the client doesn't is normal (any
    // image request mints one), so only a DIFFERENT stored value counts as a mismatch.
    const serverBearerTokenURL = serverSessionState.imageAuthorization?.bearerTokenURL ?? null;

    if (stored.imageAuthorizationUrls.some((bearerTokenURL) => bearerTokenURL !== serverBearerTokenURL)) {
      return 'outOfSync:imageAuthorization';
    }

    return null;
  };

  /**
   * Drop locally persisted data that must not be rehydrated. Extracted from resetDataIfNecessary
   * so the out-of-sync path discards on the same terms as the version/idle path, rather than
   * growing a second, subtly different notion of "stale".
   */
  this.discardStaleData = (reason) => {
    FlightRecorderService.log(`StorageService.discardStaleData:${reason}`);

    this.resetLocal();

    // EnhancementStore writes to localStorage itself via withStorageSync, so on a full disk this
    // throws from outside _writeRaw's breaker. This runs from InitService.init(), where an uncaught
    // throw leaves the splash screen up and the app never starts — clearing a cache is not worth
    // that, so log and carry on.
    try {
      $injector.get('EnhancementStore').clearEnhancementStore();
    } catch (e) {
      logError('StorageService: clearEnhancementStore failed', e);
    }
  };

  /**
   * The server-derived state localStorage claims this browser holds. Read WITHOUT dispatching
   * DeserializeAction, so InitService can compare against the server before deciding whether
   * rehydrating is safe.
   *
   * @return {{instanceId: string|null, jobNumbers: string[], imageAuthorizationUrls: (string|null)[],
   *          jobLoadTimes: number[]}}
   */
  this.getStoredSessionState = () => {
    const storedJobs = this.getData('jobData') || [];

    return {
      // Metadata about the stored data, not part of it — read as its own key, not under
      // mlt.data. where deserialize would dispatch it into a store.
      instanceId: this._read(SESSION_INSTANCE_ID_KEY),
      jobNumbers: storedJobs.map((job) => job?.jobContext?.jobNumber).filter((jobNumber) => !!jobNumber),
      // Filtered on the authorization, not the URL, so one stored without a URL still
      // contributes null and mismatches a server that has one, instead of dropping out silently.
      imageAuthorizationUrls: storedJobs
        .filter((job) => !!job?.jobContext?.imageAuthorization)
        .map((job) => job.jobContext.imageAuthorization.bearerTokenURL ?? null),
      // Jobs stored before loadedAt existed have no stamp and are left out, so they cannot expire.
      jobLoadTimes: storedJobs.map((job) => job?.jobContext?.loadedAt).filter((loadedAt) => Number.isFinite(loadedAt))
    };
  };

  this.retrieveStorage = () => {
    const $http = $injector.get('$http');

    return $http.get('localStorage').then((response) => {
      if (!response.data?.success || !response.data?.storageItems) {
        return;
      }

      response.data.storageItems.forEach((item) => {
        // saveStorage posts the serialized value, which is what both layers store — no need to
        // parse it here, and doing so would throw inside the promise on a malformed payload.
        this._writeRaw(item.key, item.value);
      });

      this.deserialize();
    });
  };

  this.saveStorage = () => {
    const $http = $injector.get('$http');

    // The endpoint stores the serialized value, which is exactly what the map already holds.
    const items = this._findKeysStartingWith(DATA_PREFIX).map((key) => ({
      key,
      value: this._readRaw(key)
    }));

    return $http.post('localStorage', { storageItems: items });
  };

  /**
   * _write / _read / _delete take a fully-qualified (already 'mlt.'-prefixed) key and must not
   * modify it — callers own the prefix.
   *
   * The map holds the SERIALIZED value, not the live object, so _read parses a fresh copy on every
   * call. Callers have always been able to mutate what get()/getData() returns without affecting
   * storage (localStorage forced a JSON round-trip), and several rely on it — CachedArrayDataFactory
   * mutates the array it is handed, and JobStore.storeJobData assigns onto the deserialize payload.
   * Caching live objects would alias the cache into those callers and let memory drift from disk
   * silently.
   */
  this._write = (key, value) => {
    this._writeRaw(key, JSON.stringify(value));
  };

  this._writeRaw = (key, raw) => {
    if (!EXTERNALLY_MANAGED_KEYS.has(key)) {
      _inMemoryStorage.set(key, raw);
    }

    if (_persistenceDisabled) {
      return;
    }

    try {
      localStorage.setItem(key, raw);
    } catch (e) {
      // Memory holds the value and _read prefers it, so the app stays correct for the rest of this
      // session — only durability past a reload is lost. Do not rethrow: updateLastAccess runs on
      // every get/set/remove and is called before set()'s own body, so a throw here would take out
      // reads and abort InitService.init(). Latch so a full disk reports once, not per write.
      _persistenceDisabled = true;
      logError(`StorageService: localStorage write failed, persistence disabled at ${key}`, e);
      FlightRecorderService.log(`StorageService:persistence-disabled:${key}`);
    }
  };

  /**
   * Memory is authoritative once a key has been seen. On a miss, fall back to localStorage and
   * backfill the map, so no bootstrap step is required and a key written by a previous page load is
   * picked up on first access. Returns the stored JSON, or null when the key exists in neither
   * layer. Backfill stores the string verbatim — writing it back through _write would re-persist a
   * value that just came off disk.
   *
   * On a cache hit the key is checked against localStorage and rewritten if it has gone missing.
   * That is repair, not re-persist: a key the mirror holds but disk does not means something outside
   * this service removed it (Safari ITP), and without the repair the recovery would be
   * in-session-only — a reload after the wipe would come back empty. The cost is one getItem per
   * cache hit, which is the deliberate price of both prompt loss detection and self-healing.
   */
  this._readRaw = (key) => {
    if (EXTERNALLY_MANAGED_KEYS.has(key)) {
      return localStorage.getItem(key);
    }

    if (_inMemoryStorage.has(key)) {
      const cached = _inMemoryStorage.get(key);

      // With persistence disabled, absent keys are expected — we stopped writing them.
      if (!_persistenceDisabled && localStorage.getItem(key) === null) {
        reportKeyLost(key);
        this._writeRaw(key, cached);
      }

      return cached;
    }

    const raw = localStorage.getItem(key);
    if (raw === null) {
      return null;
    }

    _inMemoryStorage.set(key, raw);
    return raw;
  };

  /**
   * Returns null (not undefined) when the key exists in neither layer — matches the previous
   * JSON.parse(getItem(...)) contract that resetDataIfNecessary's idle check depends on.
   */
  this._read = (key) => {
    const raw = this._readRaw(key);
    if (raw === null) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch (e) {
      logError(`StorageService: could not parse ${key}`, e);
      return null;
    }
  };

  this._delete = (key) => {
    _inMemoryStorage.delete(key);
    localStorage.removeItem(key);
  };

  this._getAllFromLocalStorage = () => Object.keys(localStorage).filter((key) => key.startsWith(KEY_PREFIX));

  /**
   * Union of both layers. Backfill is lazy, so the map alone does not know about keys written by a
   * previous page load — anything that enumerates keys (deserialize, saveStorage, resetLocal) has
   * to consult localStorage too.
   */
  this._findKeysStartingWith = (keyPrefix) => {
    const onDisk = this._getAllFromLocalStorage();

    this._reportStorageLoss(onDisk);

    const keys = new Set([..._inMemoryStorage.keys(), ...onDisk]);

    return [...keys].filter((key) => key.startsWith(keyPrefix));
  };

  /**
   * Sweep for keys the mirror holds that localStorage has lost. Called from _findKeysStartingWith,
   * which is already enumerating localStorage, so the sweep itself costs nothing — it catches keys
   * that no read has touched yet, while _readRaw catches the read ones promptly.
   */
  this._reportStorageLoss = (onDisk) => {
    const present = new Set(onDisk);

    _inMemoryStorage.forEach((value, key) => {
      if (!present.has(key)) {
        reportKeyLost(key);
      }
    });
  };

  ResetJobAction.register(this.reset);
  SessionTimeoutAction.register(this.resetLocal);
}

App.service('StorageService', [
  'ResetJobAction',
  'SessionTimeoutAction',
  'DeserializeAction',
  'CachedArrayDataFactory',
  'loggerService',
  'FlightRecorderService',
  '$injector',
  '$window',
  StorageService
]);
